// M5 · Friends, simulated (QM5_Friends): together streaks with Nudge / Cheer (POST /api/friends/:id/:action), the
// club goal, working at the same time, and the share-a-card preview (POST /api/friends/share).
import { useState } from 'preact/hooks';
import { C } from '@shared/content/characters';
import { isoWeekday } from '@shared/rules';
import { isCharId } from '../../theme';
import { useApp, friendAction, shareWithFriends, startCard, toast } from '../../store';
import { back, go } from '../../router';
import { Screen, ScrollArea, XScene, XCard, XFace, IconButton, PrimaryButton, SecondaryButton } from '../../components';
import { plural } from '../us/route';
import { ME } from './copy';
import s from './me.module.css';

export function FriendsScreen() {
  const st = useApp((x) => x.state);
  const [showChar, setShowChar] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  if (!st) return <Screen theme="margo" id="M5" />;
  const ch = st.ch, c = C[ch], f = st.friends;
  const working = f.friends.find((x) => x.working && isCharId(x.working)) || null;
  const daysLeft = 7 - isoWeekday(st.today);
  const recent = st.collection.recent[0];

  const act = async (id: string, name: string, action: 'nudge' | 'cheer') => {
    if (busy) return;
    setBusy(id);
    const r = await friendAction(id, action);
    setBusy(null);
    if (r) toast(ME.sentWave(name));
  };
  const join = async () => {
    const next = st.plan.cards.find((x) => x.st === 'todo');
    if (!next) { go('C'); return; }
    if (await startCard(next.id)) go('W', { cur: next.id, from: 'T' });
  };
  const share = async () => { setBusy('share'); const r = await shareWithFriends(); setBusy(null); if (r) toast(ME.shared); };

  return (
    <Screen theme={ch} id="M5">
      <div class={s.fGlow} aria-hidden="true"><XScene ch={ch} stage={st.buddy.stage} mode="blur" video={false} alt="" /></div>
      <div class={s.fFade} aria-hidden="true" />
      <header class={s.bar}>
        <IconButton icon="back" label="Back" onClick={() => back('M1')} />
        <h1 class={`${s.barTitle} ${s.fTitle}`}>Friends</h1>
        <span class={s.demoChip}>{ME.demo}</span>
      </header>
      <ScrollArea class={s.scroll}>
        <div class={`${s.list} ${s.fList}`}>
          <section class={s.sec} aria-label="Together streaks" style={{ gap: 6 }}>
            <p class={s.secCap} style={{ marginBottom: 4 }}>TOGETHER STREAKS</p>
            <p class={s.secNote} style={{ margin: '0 0 6px' }}>{ME.togetherNote}</p>
            {f.friends.map((x) => {
              const nudge = x.status === 'not-yet';
              const sent = !!f.sent[x.id];
              return (
                <div class={s.friend} key={x.id}>
                  <span class={s.av} aria-hidden="true" style={{ background: x.color }}>{x.initial}</span>
                  <div class={s.two} style={{ flex: 1 }}>
                    <p class={s.fT}>{x.status === 'new' || !x.together ? `${x.name} · new` : `${x.name} · ${plural(x.together, 'day')} together`}</p>
                    <p class={s.fS}>{x.status === 'not-yet' ? ME.status['not-yet'](x.name) : ME.status[x.status]}</p>
                  </div>
                  <button
                    type="button" class={nudge ? s.nudge : s.cheer} aria-disabled={sent || busy === x.id ? 'true' : undefined}
                    aria-label={sent ? `Sent to ${x.name}` : `${nudge ? 'Nudge' : 'Cheer'} ${x.name}`}
                    onClick={() => { if (!sent) void act(x.id, x.name, nudge ? 'nudge' : 'cheer'); }}
                  >{sent ? 'Sent' : nudge ? 'Nudge' : 'Cheer'}</button>
                </div>
              );
            })}
          </section>

          <section class={s.sec} aria-label={`${f.club.name} weekly goal`}>
            <p class={s.secCap}>{`${f.club.name.toUpperCase()} · WEEKLY GOAL`}</p>
            <div class={s.clubRow}>
              <p class={s.clubN}>{`${f.club.done} of ${f.club.goal} cards`}</p>
              <p class={s.small}>{daysLeft > 0 ? `${plural(daysLeft, 'day')} left` : 'Last day'}</p>
            </div>
            <div class={s.clubBar} aria-hidden="true"><div style={{ width: `${Math.min(100, Math.round((f.club.done / Math.max(1, f.club.goal)) * 100))}%` }} /></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div class={s.stack} aria-hidden="true">
                <span style={{ background: 'var(--acc)', color: 'var(--on)' }}>You</span>
                {f.friends.slice(0, 2).map((x) => <span key={x.id} style={{ background: x.color }}>{x.initial}</span>)}
              </div>
              <p class={s.small}>{ME.clubTally}</p>
            </div>
          </section>

          {working && (
            <section class={s.sec} aria-label="Work at the same time">
              <p class={s.secCap}>WORK AT THE SAME TIME</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span class={s.av} aria-hidden="true" style={{ background: working.color }}>{working.initial}<span class={s.online} /></span>
                <p class={s.fT} style={{ flex: 1, fontWeight: 600, lineHeight: 1.4 }}>{`${working.name} is working with ${C[working.working as 'sunny'].name} right now`}</p>
              </div>
              <SecondaryButton h={52} style={{ background: 'rgba(15,14,12,0.45)' }} onClick={() => void join()}>Join for 25 min</SecondaryButton>
            </section>
          )}

          <section class={s.sec} aria-label="Share a card" style={{ gap: 14 }}>
            <p class={s.secCap}>SHARE A CARD</p>
            <div class={s.shareCard}>
              <div class={s.shareMini} aria-hidden="true">
                <XCard ch={ch} face="front" w={160} h={110} title={recent ? recent.title : 'Call the dentist'} stamped stamp="DONE" foot="" tilt={-2} />
              </div>
              <div class={s.two} style={{ flex: 1, gap: 4 }}>
                <p class={s.shareBig}>{`${st.streak.count}-day streak`}</p>
                <p class={s.shareSub}>{`${plural(st.collection.cardsDone, 'card')} so far`}</p>
                {showChar && <div class={s.with}><XFace ch={ch} e={1} s={28} alt="" /><span>{`with ${c.name}`}</span></div>}
                <p class={s.mark}>XIAOMAN</p>
              </div>
            </div>
            <button type="button" class={s.toggleRow} role="switch" aria-checked={showChar ? 'true' : 'false'} onClick={() => setShowChar(!showChar)}>
              <span>{`Show ${c.name} on the card`}</span>
              <span class={s.toggle} data-on={showChar ? 'true' : 'false'} aria-hidden="true"><span /></span>
            </button>
            <PrimaryButton h={56} busy={busy === 'share'} onClick={() => void share()}>Share</PrimaryButton>
          </section>
        </div>
      </ScrollArea>
    </Screen>
  );
}
